/**
 * Route constants shared by the customer portal and internal admin console (Doc §5.2 step 2,
 * §5.3). Each entry maps a portal path to the scope that gates it, so the sidebar and the route
 * guards read from one source. The route lists are also what `scripts/auditCustomerPortal.ts` and
 * `scripts/auditInternalAdmin.ts` assert against (Doc 2 Appendix A).
 */

import { can, type CustomerPortalRole, type CustomerPortalScope } from './roles';

/** The deployed CVSI developer-docs portal (apps/docs-portal). */
export const CVSI_DOCS_URL = 'https://cruze-verify-docs-portal.web.app';

/**
 * Build a link to the docs portal that carries where to return to. The docs portal reads
 * `returnTo`/`returnLabel` off the query string and shows a "back to X" banner when present — see
 * apps/docs-portal/src/App.tsx. Only meaningful in a browser (reads `window.location`).
 */
export function cvsiDocsUrl(returnLabel: string): string {
  const params = new URLSearchParams({ returnTo: window.location.href, returnLabel });
  return `${CVSI_DOCS_URL}/?${params.toString()}`;
}

export interface PortalRoute {
  path: string;
  label: string;
  /** Scope required to see/use the page; null = always visible to any authenticated member. */
  scope: CustomerPortalScope | null;
}

/** Customer portal navigation (Doc §5.1 / §5.3). */
export const CUSTOMER_ROUTES: PortalRoute[] = [
  { path: '/dashboard', label: 'Mission Control Dashboard', scope: 'tenant:read' },
  { path: '/webhooks', label: 'Event Delivery', scope: 'webhooks:read' },
  { path: '/policies', label: 'Verification Governance', scope: 'policies:read' },
  { path: '/reports', label: 'Safety Intelligence', scope: 'reports:read' },
  { path: '/safety-intelligence', label: 'Risk & Forecast Signals', scope: 'reports:read' },
  { path: '/activity', label: 'Live Activity', scope: 'events:read' },
  { path: '/events', label: 'Movement Events', scope: 'events:read' },
  { path: '/corridors', label: 'Operational Intelligence', scope: 'reports:read' },
  { path: '/usage', label: 'Infrastructure Usage', scope: 'usage:read' },
  { path: '/billing', label: 'Billing', scope: 'billing:read' },
  { path: '/support', label: 'Support', scope: 'support:read' },
  { path: '/security', label: 'Trust Center', scope: 'audit:read' },
  { path: '/audit', label: 'Audit Center', scope: 'audit:read' },
  { path: '/settings', label: 'Tenant Administration', scope: 'tenant:read' },
];

/** The bare paths the route-audit script requires to exist (Doc 2 Appendix A1). */
export const REQUIRED_CUSTOMER_ROUTES = CUSTOMER_ROUTES.map((r) => r.path);

/**
 * Role-based workspace grouping for the customer portal sidebar (UX Implementation Guide §3.1/§3.2:
 * six workspaces replace the flat item list). Each group lists the route `path`s it contains, in
 * display order; the sidebar renders one group heading per entry with its member routes nested below.
 */
export interface CustomerWorkspace {
  label: string;
  paths: string[];
  description: string;
  tone: 'mission' | 'operations' | 'intelligence' | 'trust' | 'governance';
}

export const CUSTOMER_WORKSPACES: CustomerWorkspace[] = [
  { label: 'Mission Control', paths: ['/dashboard'], description: 'Posture and next actions', tone: 'mission' },
  { label: 'Movement Operations', paths: ['/activity', '/events'], description: 'Live activity and event review', tone: 'operations' },
  { label: 'Intelligence', paths: ['/reports', '/safety-intelligence', '/corridors'], description: 'Safety analysis and forecasts', tone: 'intelligence' },
  { label: 'Trust & Evidence', paths: ['/security', '/audit'], description: 'Integrity and proof', tone: 'trust' },
  { label: 'Governance & Administration', paths: ['/policies', '/webhooks', '/usage', '/billing', '/settings'], description: 'Controls and configuration', tone: 'governance' },
];

/** Routes shown outside any workspace group (utility links, not part of the primary IA). */
export const CUSTOMER_UNGROUPED_ROUTES = ['/support'];

/**
 * Where a role should land after login — the first workspace route (in CUSTOMER_WORKSPACES order)
 * that role actually has scope for, instead of hard-coding '/dashboard' for everyone. Matters
 * concretely for `billing`, which lacks `tenant:read` and so cannot see '/dashboard' at all
 * (Finish-Line Guide §2/§4, "persona-specific default landing workspace").
 */
export function defaultLandingPath(role: CustomerPortalRole): string {
  for (const group of CUSTOMER_WORKSPACES) {
    const path = group.paths.find((p) => {
      const route = CUSTOMER_ROUTES.find((r) => r.path === p);
      return route && (route.scope == null || can(role, route.scope));
    });
    if (path) return path;
  }
  return '/dashboard';
}

/** Internal admin console navigation (Doc §6.1). Gated by CVSI-staff roles, not tenant scopes. */
export const INTERNAL_ADMIN_ROUTES = [
  { path: '/tenants', label: 'Tenants' },
  { path: '/tenant-approvals', label: 'Tenant Approvals' },
  { path: '/live-access', label: 'Live Access Review' },
  { path: '/review-queue', label: 'Review Queue' },
  { path: '/security-events', label: 'Security Events' },
  { path: '/audit-chain', label: 'Audit Chain' },
  { path: '/support-queue', label: 'Support Queue' },
  { path: '/billing-review', label: 'Billing Review' },
  { path: '/demo-tenants', label: 'Demo Tenants' },
  { path: '/release-readiness', label: 'Release Readiness' },
  { path: '/system-health', label: 'System Health' },
  { path: '/safety-intelligence', label: 'Safety Intelligence' },
] as const;

export const REQUIRED_INTERNAL_ADMIN_ROUTES = INTERNAL_ADMIN_ROUTES.map((r) => r.path);

/** Role-based workspace grouping for the internal admin sidebar, mirroring CUSTOMER_WORKSPACES. */
export const INTERNAL_ADMIN_WORKSPACES: { label: string; paths: string[] }[] = [
  { label: 'Tenants', paths: ['/tenants', '/tenant-approvals', '/demo-tenants'] },
  { label: 'Operations & Trust', paths: ['/live-access', '/review-queue', '/security-events', '/audit-chain'] },
  { label: 'Support & Billing', paths: ['/support-queue', '/billing-review'] },
  { label: 'Platform', paths: ['/release-readiness', '/system-health', '/safety-intelligence'] },
];

/** Backend API paths the portal client targets (under the tenant-scoped /v1 router). */
export const API = {
  tenant: '/admin/tenant',
  tenantVerificationEmail: '/admin/tenant/verification-email',
  tenantSuspend: '/admin/tenant/suspend',
  tenantReactivate: '/admin/tenant/reactivate',
  keys: '/admin/keys',
  key: (id: string) => `/admin/keys/${id}`,
  keyTelemetry: (id: string) => `/admin/keys/${id}/telemetry`,
  keyRotate: (id: string) => `/admin/keys/${id}/rotate`,
  keyRevoke: (id: string) => `/admin/keys/${id}/revoke`,
  policies: '/admin/policies',
  branding: '/admin/branding',
  webhooks: '/admin/webhooks',
  webhook: (id: string) => `/admin/webhooks/${id}`,
  webhookTest: '/webhooks/test',
  usage: '/admin/usage',
  audit: '/admin/audit',
  auditExport: '/admin/audit/export',
  auditVerify: '/admin/audit/verify',
  securityEvidence: '/admin/security/evidence',
  retentionStatus: '/admin/retention-status',
  reviewQueue: '/admin/review-queue',
  reviewQueueAction: (id: string) => `/admin/review-queue/${encodeURIComponent(id)}/customer-action`,
  billingSummary: '/admin/billing/summary',
  billingLineItems: '/admin/billing/line-items',
  activeMovements: '/admin/active-movements',
  supportTickets: '/admin/support/tickets',
  supportTicket: (id: string) => `/admin/support/tickets/${id}`,
  supportTicketMessages: (id: string) => `/admin/support/tickets/${id}/messages`,
  supportTicketStatus: (id: string) => `/admin/support/tickets/${id}/status`,
  reports: '/reports',
  report: (id: string) => `/reports/${id}`,
  reportExport: (id: string, format: 'pdf' | 'csv' | 'json' | 'docx') => `/reports/${id}/export?format=${format}`,
  rideDetailExport: (externalRef: string) => `/reports/rides/${encodeURIComponent(externalRef)}/detail/export`,
  analyticsSummary: (start?: string, end?: string) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const qs = params.toString();
    return `/reports/analytics-summary${qs ? `?${qs}` : ''}`;
  },
  corridors: '/corridors',
  corridor: (id: string) => `/corridors/${id}`,
  corridorForecast: (id: string, horizons?: string) => `/corridors/${id}/forecast${horizons ? `?horizons=${horizons}` : ''}`,
  corridorForecastExport: (id: string, format: 'geojson' | 'csv') => `/corridors/${id}/forecast.${format}`,
  eventsMovement: '/events/movement',
  eventMovement: (id: string) => `/events/movement/${id}`,
  reasonCodes: '/reason-codes',
  safetyForecast: '/safety-intelligence/forecast',
  safetyAnomalies: '/safety-intelligence/anomalies',
} as const;
